/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';

import { styleguideColors as COLORS, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import { DEFAULT_PROPERTY_OVERVIEW_SHARED_STYLES, type PropertyOverviewSharedStyles } from './propertyOverviewStyles';

export type NewJobModalStyles = {
  shared: PropertyOverviewSharedStyles;
  dialog: SerializedStyles;
  header: SerializedStyles;
  /** Holds the scrolling fields and the pinned footer. */
  form: SerializedStyles;
  title: SerializedStyles;
  subtitle: SerializedStyles;
  close: SerializedStyles;
  body: SerializedStyles;
  group: SerializedStyles;
  groupTitle: SerializedStyles;
  fields: SerializedStyles;
  field: SerializedStyles;
  wideField: SerializedStyles;
  label: SerializedStyles;
  optional: SerializedStyles;
  input: SerializedStyles;
  hint: SerializedStyles;
  error: SerializedStyles;
  chips: SerializedStyles;
  chip: SerializedStyles;
  chipRemove: SerializedStyles;
  footer: SerializedStyles;
  blocker: SerializedStyles;
};

export const DEFAULT_NEW_JOB_MODAL_STYLES: NewJobModalStyles = {
  shared: DEFAULT_PROPERTY_OVERVIEW_SHARED_STYLES,
  /* The dialog never grows past the window: its header and footer stay put and only the fields
     between them scroll, so Cancel and Create job are always in reach. ModalShell keeps a 20px
     margin around its content, hence the 40px. */
  dialog: css`
    label: NEW_JOB_MODAL;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 40px);
    background: ${COLORS.white};
    border: 1px solid ${COLORS.stroke};
  `,
  header: css`
    label: NEW_JOB_MODAL_HEADER;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    flex-shrink: 0;
    padding: 18px 20px 14px;
    border-bottom: 1px solid ${COLORS.stroke};
  `,
  form: css`
    label: NEW_JOB_MODAL_FORM;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  `,
  title: css`
    label: NEW_JOB_MODAL_TITLE;
    margin: 0;
    font-family: ${FONT.display};
    font-size: ${pxToRem(20)};
    font-weight: ${FONT_WEIGHT.bold};
    color: ${COLORS.ink};
  `,
  subtitle: css`
    label: NEW_JOB_MODAL_SUBTITLE;
    margin-top: 2px;
    font-family: ${FONT.body};
    font-size: ${pxToRem(13)};
    color: ${COLORS.textSecondary};
  `,
  close: css`
    label: NEW_JOB_MODAL_CLOSE;
    margin-left: auto;
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border: 1px solid ${COLORS.stroke};
    background: ${COLORS.white};
    color: ${COLORS.midtone};
    font-size: ${pxToRem(16)};
    line-height: 1;
    cursor: pointer;

    &:hover {
      border-color: ${COLORS.ink};
      color: ${COLORS.ink};
    }
  `,
  body: css`
    label: NEW_JOB_MODAL_BODY;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 18px 20px;
    min-height: 0;
    overflow-y: auto;
  `,
  group: css`
    label: NEW_JOB_MODAL_GROUP;
    margin: 0;
    padding: 0;
    border: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  `,
  groupTitle: css`
    label: NEW_JOB_MODAL_GROUP_TITLE;
    padding: 0;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(9.5)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${COLORS.midtone};
  `,
  fields: css`
    label: NEW_JOB_MODAL_FIELDS;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 16px;

    @media (max-width: 560px) {
      grid-template-columns: minmax(0, 1fr);
    }
  `,
  field: css`
    label: NEW_JOB_MODAL_FIELD;
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
  `,
  wideField: css`
    label: NEW_JOB_MODAL_FIELD_WIDE;
    grid-column: 1 / -1;
  `,
  label: css`
    label: NEW_JOB_MODAL_LABEL;
    font-family: ${FONT.body};
    font-size: ${pxToRem(12.5)};
    font-weight: ${FONT_WEIGHT.bold};
    color: ${COLORS.ink};
  `,
  optional: css`
    label: NEW_JOB_MODAL_OPTIONAL;
    margin-left: 6px;
    font-weight: normal;
    color: ${COLORS.midtone};
  `,
  input: css`
    label: NEW_JOB_MODAL_INPUT;
    width: 100%;
    box-sizing: border-box;
    padding: 8px 10px;
    border: 1px solid ${COLORS.stroke};
    background: ${COLORS.white};
    font-family: ${FONT.body};
    font-size: ${pxToRem(13.5)};
    color: ${COLORS.ink};
    resize: vertical;

    &:focus {
      outline: none;
      border-color: ${COLORS.ink};
    }
  `,
  hint: css`
    label: NEW_JOB_MODAL_HINT;
    font-family: ${FONT.body};
    font-size: ${pxToRem(12)};
    color: ${COLORS.midtone};
  `,
  error: css`
    label: NEW_JOB_MODAL_ERROR;
    font-family: ${FONT.body};
    font-size: ${pxToRem(12)};
    color: ${COLORS.error};
  `,
  chips: css`
    label: NEW_JOB_MODAL_CHIPS;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  `,
  chip: css`
    label: NEW_JOB_MODAL_CHIP;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 6px 4px 9px;
    background: ${COLORS.sunken};
    font-family: ${FONT.body};
    font-size: ${pxToRem(12.5)};
    color: ${COLORS.ink};
  `,
  chipRemove: css`
    label: NEW_JOB_MODAL_CHIP_REMOVE;
    border: 0;
    padding: 0 2px;
    background: none;
    color: ${COLORS.midtone};
    cursor: pointer;

    &:hover {
      color: ${COLORS.ink};
    }
  `,
  footer: css`
    label: NEW_JOB_MODAL_FOOTER;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    flex-shrink: 0;
    padding: 14px 20px;
    border-top: 1px solid ${COLORS.stroke};
  `,
  blocker: css`
    label: NEW_JOB_MODAL_BLOCKER;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    font-family: ${FONT.body};
    font-size: ${pxToRem(13.5)};
    color: ${COLORS.ink};
  `,
};
